import { useState } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Search,
  Trash2,
  RotateCcw,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  api,
  apiError,
  type Paginated,
  type Ticket as TicketType,
} from "../lib/api";
import { GlassCard } from "../components/ui/GlassCard";
import { EmptyState } from "../components/ui/EmptyState";
import { PriorityBadge } from "../components/tickets/PriorityBadge";
import { StatusBadge } from "../components/tickets/StatusBadge";
import { CategoryBadge } from "../components/tickets/CategoryBadge";
import { fmtRelative } from "../lib/format";
import { toast } from "sonner";

let searchDebounce: ReturnType<typeof setTimeout> | undefined;

export default function RecycleBin() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const q = search.get("q") || "";
  const page = parseInt(search.get("page") || "1", 10);

  // State for confirm modal
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (!value) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") next.delete("page");
    setSearch(next, { replace: true });
  };

  const { data, isLoading } = useQuery({
    queryKey: ["tickets.deleted", { q, page }],
    queryFn: async () => {
      const res = await api.get<Paginated<TicketType>>("/api/tickets", {
        params: { q, page, pageSize: 20, deleted: true },
      });
      return res.data;
    },
    placeholderData: keepPreviousData,
  });

  // Restore Mutation
  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/tickets/${id}/restore`);
      return res.data as { ticket: TicketType };
    },
    onSuccess: (data) => {
      toast.success(`Ticket "${data.ticket.title}" successfully restored!`);
      queryClient.invalidateQueries({ queryKey: ["tickets.deleted"] });
      queryClient.invalidateQueries({ queryKey: ["tickets.list"] });
    },
    onError: (err) => {
      toast.error(apiError(err) || "Failed to restore ticket");
    },
  });

  // Permanent Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/api/tickets/${id}`);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Ticket deleted permanently.");
      setConfirmDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ["tickets.deleted"] });
    },
    onError: (err) => {
      toast.error(apiError(err) || "Failed to delete ticket permanently");
      setConfirmDeleteId(null);
    },
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="page-title">Recycle Bin</h1>
          <p className="page-subtitle">
            Manage soft-deleted tickets. Restore them to active desk or delete
            permanently.
          </p>
        </div>

        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search deleted tickets..."
            defaultValue={q}
            onChange={(e) => {
              const v = e.target.value;
              clearTimeout(searchDebounce);
              searchDebounce = setTimeout(() => updateParam("q", v), 350);
            }}
            className="input pl-10"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton h-16 rounded-2xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <GlassCard>
          <EmptyState
            icon={Trash2}
            title="Recycle bin is empty"
            description={
              q
                ? "No deleted tickets match your search."
                : "Tickets deleted by IT or Admins will appear here."
            }
          />
        </GlassCard>
      ) : (
        <GlassCard padded={false}>
          <div className="divide-y divide-white/5">
            {items.map((t) => (
              <div
                key={t.id}
                className="grid grid-cols-12 gap-3 items-center px-4 py-4 hover:bg-white/[0.02] transition-colors"
              >
                <div className="col-span-12 lg:col-span-6 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 font-mono">
                      #{t.id.slice(-6)}
                    </span>
                    <div className="text-sm text-white font-medium truncate">
                      {t.title}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 truncate mt-1">
                    {t.description}
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-500 uppercase tracking-wider">
                    <span>Deleted {fmtRelative(t.updatedAt)}</span>
                    <span>·</span>
                    <span>Created by {t.submitter?.name}</span>
                  </div>
                </div>

                <div className="col-span-6 lg:col-span-2 flex items-center gap-1.5 flex-wrap">
                  <PriorityBadge priority={t.priority} />
                  <CategoryBadge category={t.category} />
                </div>

                <div className="col-span-3 lg:col-span-1">
                  <StatusBadge status={t.status} />
                </div>

                <div className="col-span-3 lg:col-span-3 flex justify-end items-center gap-2">
                  <button
                    onClick={() => restoreMutation.mutate(t.id)}
                    disabled={restoreMutation.isPending}
                    className="btn-secondary py-1.5 px-3 text-xs flex items-center gap-1.5 hover:text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500/20"
                    title="Restore ticket"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Restore
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(t.id)}
                    className="btn-secondary py-1.5 px-3 text-xs flex items-center gap-1.5 text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/20"
                    title="Delete permanently"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Purge
                  </button>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            disabled={page <= 1}
            onClick={() => updateParam("page", String(page - 1))}
            className="btn-ghost disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <span className="text-sm text-slate-300 px-3">
            Page <b className="text-white">{page}</b> of {totalPages} (Total:{" "}
            {total})
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => updateParam("page", String(page + 1))}
            className="btn-ghost disabled:opacity-40"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <GlassCard strong className="max-w-md w-full p-6 space-y-4 animate-fade-in">
            <div className="flex items-center gap-3 text-rose-400">
              <div className="p-2 bg-rose-500/10 border border-rose-500/20 rounded-xl">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <h2 className="text-lg font-bold text-white">Delete Permanently?</h2>
            </div>

            <p className="text-sm text-slate-300">
              This action is <b className="text-rose-400">irreversible</b>. The
              ticket, all comments, and attachments will be permanently purged from
              the database.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="btn-secondary"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(confirmDeleteId)}
                className="btn-critical flex items-center gap-1.5"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting..." : "Confirm Purge"}
              </button>
            </div>
          </GlassCard>
        </div>
      )}
    </div>
  );
}
